import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const EmployerVerificationForm = ({ formData, errors, onChange }) => {
  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1000+', label: '1000+ employees' },
  ];

  const industryOptions = [
    { value: 'tech', label: 'Technology' },
    { value: 'construction', label: 'Construction' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'finance', label: 'Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'retail', label: 'Retail' },
  ];

  const hiringVolumeOptions = [
    { value: '1-5', label: '1-5 per year' },
    { value: '6-10', label: '6-10 per year' },
    { value: '11-25', label: '11-25 per year' },
    { value: '25+', label: '25+ per year' },
  ];

  const teamSizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-25', label: '11-25 employees' },
    { value: '26-50', label: '26-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-250', label: '101-250 employees' },
    { value: '250+', label: '250+ employees' },
  ];

  const hrBudgetOptions = [
    { value: 'under-100k', label: 'Under $100K' },
    { value: '100k-250k', label: '$100K - $250K' },
    { value: '250k-500k', label: '$250K - $500K' },
    { value: '500k-1m', label: '$500K - $1M' },
    { value: '1m-5m', label: '$1M - $5M' },
    { value: '5m+', label: '$5M+' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl md:text-2xl font-semibold text-[#111827] mb-2">
          Employer Verification
        </h3>
        <p className="text-sm md:text-base text-[#6B7280]">
          Please provide your company and hiring information
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          value={formData?.fullName || ''}
          onChange={(e) => onChange('fullName', e?.target?.value)}
          error={errors?.fullName}
          required
        />

        <Input
          label="Work Email Address"
          type="email"
          placeholder="your.name@company.com"
          description="Must be company domain email"
          value={formData?.email || ''}
          onChange={(e) => onChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Company Name"
          type="text"
          placeholder="Enter company name"
          value={formData?.companyName || ''}
          onChange={(e) => onChange('companyName', e?.target?.value)}
          error={errors?.companyName}
          required
        />

        <Input
          label="Job Title"
          type="text"
          placeholder="Your position"
          value={formData?.jobTitle || ''}
          onChange={(e) => onChange('jobTitle', e?.target?.value)}
          error={errors?.jobTitle}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Select
          label="Company Size"
          placeholder="Select company size"
          options={companySizeOptions}
          value={formData?.companySize || ''}
          onChange={(value) => onChange('companySize', value)}
          error={errors?.companySize}
          required
        />

        <Select
          label="Industry"
          placeholder="Select industry"
          options={industryOptions}
          value={formData?.industry || ''}
          onChange={(value) => onChange('industry', value)}
          error={errors?.industry}
          required
        />
      </div>
      <Select
        label="Annual Senior Hiring Volume"
        placeholder="Select hiring volume"
        options={hiringVolumeOptions}
        value={formData?.hiringVolume || ''}
        onChange={(value) => onChange('hiringVolume', value)}
        error={errors?.hiringVolume}
        required
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Select
          label="Team Size"
          placeholder="Select team size"
          description="Number of employees you manage"
          options={teamSizeOptions}
          value={formData?.teamSize || ''}
          onChange={(value) => onChange('teamSize', value)}
          error={errors?.teamSize}
          required
        />

        <Select
          label="Annual HR Budget"
          placeholder="Select budget range"
          description="Approximate annual hiring budget"
          options={hrBudgetOptions}
          value={formData?.hrBudget || ''}
          onChange={(value) => onChange('hrBudget', value)}
          error={errors?.hrBudget}
          required
        />
      </div>
      <Input
        label="Company Website"
        type="url"
        placeholder="https://www.company.com"
        value={formData?.companyWebsite || ''}
        onChange={(e) => onChange('companyWebsite', e?.target?.value)}
        error={errors?.companyWebsite}
        required
      />
      <Input
        label="LinkedIn Company Page"
        type="url"
        placeholder="https://linkedin.com/company/yourcompany"
        description="Optional - helps us verify your company"
        value={formData?.linkedinUrl || ''}
        onChange={(e) => onChange('linkedinUrl', e?.target?.value)}
      />
      <div className="pt-4 border-t border-[#E5E7EB] space-y-4">
        <Checkbox
          label="I confirm I have direct hiring authority in my organization"
          checked={formData?.hiringAuthorityConfirmed || false}
          onChange={(e) => onChange('hiringAuthorityConfirmed', e?.target?.checked)}
          error={errors?.hiringAuthorityConfirmed}
          required
        />

        <Checkbox
          label="I confirm my company hires directly (not a recruitment agency)"
          checked={formData?.directHiringConfirmed || false}
          onChange={(e) => onChange('directHiringConfirmed', e?.target?.checked)}
          error={errors?.directHiringConfirmed}
          required
        />

        <Checkbox
          label="I understand BlinD is a subscription-based platform"
          checked={formData?.subscriptionAcknowledged || false}
          onChange={(e) => onChange('subscriptionAcknowledged', e?.target?.checked)}
          error={errors?.subscriptionAcknowledged}
          required
        />
      </div>
    </div>
  );
};

export default EmployerVerificationForm;